import * as React from 'react';
import { createPortal } from 'react-dom';

interface BreadcrumbActionsSlotContextType {
  outletElement: HTMLDivElement | null;
  setOutletElement: (el: HTMLDivElement | null) => void;
}

const BreadcrumbActionsSlotContext = React.createContext<BreadcrumbActionsSlotContextType | null>(null);

/**
 * Provider component that wraps the template and creates a ref for the outlet.
 * Must wrap both the Outlet and any Content components.
 */
function Provider({ children }: React.PropsWithChildren) {
  const [outletElement, setOutletElement] = React.useState<HTMLDivElement | null>(null);

  const value = React.useMemo(() => ({ outletElement, setOutletElement }), [outletElement]);

  return <BreadcrumbActionsSlotContext.Provider value={value}>{children}</BreadcrumbActionsSlotContext.Provider>;
}

/**
 * Outlet component that renders in the breadcrumbs bar.
 * This is where the action buttons will be portaled to.
 */
function Outlet({ className }: { className?: string }) {
  const context = React.useContext(BreadcrumbActionsSlotContext);

  if (!context) {
    throw new Error('BreadcrumbActionsSlot.Outlet must be used within BreadcrumbActionsSlot.Provider');
  }

  return <div ref={context.setOutletElement} className={className} />;
}

/**
 * Content component that portals its children to the Outlet.
 * Use this to render action buttons from anywhere in the component tree.
 */
function Content({ children }: React.PropsWithChildren) {
  const context = React.useContext(BreadcrumbActionsSlotContext);

  if (!context) {
    throw new Error('BreadcrumbActionsSlot.Content must be used within BreadcrumbActionsSlot.Provider');
  }

  // Only render portal when outlet element is available
  if (!context.outletElement) {
    return null;
  }

  return createPortal(children, context.outletElement);
}

export const BreadcrumbActionsSlot = {
  Provider,
  Outlet,
  Content,
};
